import React, { useEffect, useState } from 'react'
import { ScrollText } from 'lucide-react'
import { Button } from '@quest-lock/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@quest-lock/ui/dialog'
import { useContactForm } from '@/components/contact/hooks'
import { ContactFormFields } from '@/components/contact/ContactFormFields'
import ContactTypeSelector from '@/components/contact/ContactTypeSelector'

const ContactMessageFloating: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const {
    isLoading,
    formData,
    errors,
    rating,
    isConsentChecked,
    canSubmit,
    setRating,
    setIsConsentChecked,
    handleInputChange,
    handleSubmit,
    handleTurnstileChange
  } = useContactForm(() => {
    setIsModalOpen(false)
  })

  const [step, setStep] = useState<'choose' | 'form'>('choose')

  useEffect(() => {
    if (isModalOpen) setStep('choose')
  }, [isModalOpen])

  const handleCategorySelect = (value: string) => {
    handleInputChange('category', value)
    setStep('form')
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogTrigger asChild>
        <Button
          aria-label="Contact Us Button"
          className="border-shire-bark bg-shire-light text-shire-light hover:bg-shire-light fixed right-5 bottom-5 z-40 size-16 rounded-full border-2 border-dashed p-4 shadow-lg hover:scale-105 sm:size-20"
        >
          <ScrollText
            size={48}
            className="stroke-shire-bark !h-6 !w-6 sm:!h-8 sm:!w-8"
          />
        </Button>
      </DialogTrigger>
      <DialogContent className="border-shire-bark bg-background font-body text-foreground max-h-[80vh] max-w-sm sm:max-h-[85vh] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Send a Raven
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground">
            We'd love to hear from you. Let us know what's on your mind.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 sm:space-y-6 sm:py-4">
          {step === 'choose' ? (
            <ContactTypeSelector
              value={formData.category}
              onSelect={handleCategorySelect}
            />
          ) : (
            <>
              <div className="-mb-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setStep('choose')}
                  className="font-ui"
                >
                  Back to category selection
                </Button>
              </div>
              <ContactFormFields
                formData={formData}
                errors={errors}
                rating={rating}
                isConsentChecked={isConsentChecked}
                handleInputChange={handleInputChange}
                setRating={setRating}
                setIsConsentChecked={setIsConsentChecked}
                handleTurnstileChange={handleTurnstileChange}
              />
            </>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsModalOpen(false)}
            disabled={isLoading}
            className="font-ui"
          >
            {step === 'choose' ? 'Close' : 'Cancel'}
          </Button>
          {step === 'form' && (
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`${!canSubmit ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {isLoading ? 'Sending...' : 'Send Raven'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ContactMessageFloating
